// src/components/RaydiumCreationForm.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Rocket,
  TrendingUp,
  AlertCircle,
  Info,
  Loader2,
  X,
  Image as ImageIcon,
  ExternalLink,
  Lock,
  Flame,
  DollarSign,
  Settings,
  Droplet,
  Package,
  Check,
  CheckCircle,
  Shield,
  Zap
} from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { PublicKey } from '@solana/web3.js';
import { SOLANA_RPC_ENDPOINT } from '../utils/constants';
import raydiumService from '../services/raydium.service';
import { apiEndpoints } from '../services/api.service';
import { bundlerService } from '../services/frontend-bundler/bundler.service';

interface RaydiumFormData {
  name: string;
  symbol: string;
  description: string;
  supply: number;
  decimals: number;
  imageUrl: string;
  website: string;
  twitter: string;
  telegram: string;
  discord: string;
}

interface PoolSettings {
  poolType: 'CPMM' | 'AMM_V4' | 'CLMM';
  initialLiquiditySOL: number;
  baseTokenAmount: number;
  feeTier: number;
  startTime: 'now' | 'custom';
  customStartTime?: string;
  lockLiquidity: boolean;
  lockDuration: number; // days
  burnAndEarn: boolean;
  distribution: {
    liquidity: number; // percentage
    team: number;
    marketing: number;
    treasury: number;
  };
}

const RaydiumCreationForm: React.FC = () => {
  const { isConnected, publicKey } = useWallet();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Form state
  const [formData, setFormData] = useState<RaydiumFormData>({
    name: '',
    symbol: '',
    description: '',
    supply: 1000000000, // 1 billion default
    decimals: 9,
    imageUrl: '',
    website: '',
    twitter: '',
    telegram: '',
    discord: ''
  });

  const [poolSettings, setPoolSettings] = useState<PoolSettings>({
    poolType: 'CPMM',
    initialLiquiditySOL: 10,
    baseTokenAmount: 800000000, // 80% of supply
    feeTier: 25, // 0.25%
    startTime: 'now',
    lockLiquidity: true,
    lockDuration: 30,
    burnAndEarn: true,
    distribution: {
      liquidity: 80,
      team: 10,
      marketing: 5,
      treasury: 5
    }
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [step, setStep] = useState<'form' | 'creating' | 'success'>('form');
  const [createdTokenAddress, setCreatedTokenAddress] = useState<string>('');
  const [poolAddress, setPoolAddress] = useState<string>('');
  const [txSignature, setTxSignature] = useState<string>('');

  // Bundle state
  const [bundles, setBundles] = useState<any[]>([]);
  const [selectedBundle, setSelectedBundle] = useState<any>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [bundlePassword, setBundlePassword] = useState('');
  const [showBundlePasswordModal, setShowBundlePasswordModal] = useState(false);
  const [bundleBuyProgress, setBundleBuyProgress] = useState<{
    total: number;
    completed: number;
    successful: number;
    failed: number;
    status: string;
  }>({ total: 0, completed: 0, successful: 0, failed: 0, status: '' });

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const FEE_TIERS = {
    CPMM: [
      { label: '0.01%', value: 1 },
      { label: '0.05%', value: 5 },
      { label: '0.25%', value: 25 },
      { label: '1%', value: 100 }
    ],
    CLMM: [
      { label: '0.01%', value: 100 },
      { label: '0.05%', value: 500 },
      { label: '0.3%', value: 3000 },
      { label: '1%', value: 10000 }
    ]
  };

  // Load bundles when wallet connects
  useEffect(() => {
    if (isConnected && publicKey) {
      loadUserBundles();
    }
  }, [isConnected, publicKey]);

  const loadUserBundles = async () => {
    if (!publicKey) return;
    try {
      const userBundles = await bundlerService.getUserBundles(publicKey.toString());
      setBundles(userBundles);
      
      // Check if there's an active unlocked bundle
      const currentBundle = bundlerService.getCurrentBundle();
      if (currentBundle && !bundlerService.getIsLocked()) {
        setSelectedBundle(currentBundle);
        setIsUnlocked(true);
      }
    } catch (error) {
      console.error('Failed to load bundles:', error);
    }
  };

  const unlockSelectedBundle = async () => {
    if (!selectedBundle || !bundlePassword) return;
    
    try {
      const unlockedBundle = await bundlerService.unlockBundle(
        selectedBundle.id, 
        bundlePassword
      );
      setIsUnlocked(true);
      setShowBundlePasswordModal(false);
      setBundlePassword('');
      console.log(`✅ Bundle "${unlockedBundle.name}" unlocked successfully!`);
    } catch (error: any) {
      alert(`Failed to unlock bundle: ${error.message}`);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) {
      alert('Only PNG, JPG, GIF, WEBP are allowed');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert(`File too large (max ${MAX_FILE_SIZE / 1024 / 1024}MB)`);
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use the same endpoint as PumpFun with a different folder
      const response = await fetch(
        `${apiEndpoints.uploadLogo}?folder=raydium-tokens&prefix=raydium`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }
      
      const data = await response.json();
      
      // Check for the URL in the response (could be secure_url or url)
      const imageUrl = data.secure_url || data.url || data.imageUrl;
      
      if (!imageUrl) {
        throw new Error('No image URL in response');
      }
      
      setFormData(prev => ({ ...prev, imageUrl }));
      
      console.log('✅ Image uploaded successfully:', imageUrl);
      
    } catch (error: any) {
      console.error('Upload error:', error);
      
      // Fallback to base64 encoding if upload fails
      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          setFormData(prev => ({ ...prev, imageUrl: base64 }));
          console.log('📦 Using base64 fallback');
        };
        reader.readAsDataURL(file);
      } catch (fallbackError) {
        alert('Failed to upload image. Please try again.');
      }
    } finally {
      setUploadingLogo(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!formData.name || formData.name.length > 50) {
      errors.push('Token name is required (max 50 characters)');
    }
    if (!formData.symbol || formData.symbol.length > 10) {
      errors.push('Token symbol is required (max 10 characters)');
    }
    if (!formData.description || formData.description.length > 500) {
      errors.push('Description is required (max 500 characters)');
    }
    if (!formData.imageUrl) {
      errors.push('Token logo is required');
    }
    if (formData.supply < 1 || formData.supply > 1000000000000) {
      errors.push('Invalid supply amount');
    }
    if (poolSettings.initialLiquiditySOL < 1) {
      errors.push('Minimum liquidity is 1 SOL');
    }
    if (!isConnected || !publicKey) {
      errors.push('Please connect your wallet');
    }

    if (errors.length > 0) {
      alert(errors.join('\n'));
      return false;
    }

    return true;
  };

  const calculateInitialPrice = () => {
    const baseAmount = poolSettings.baseTokenAmount;
    const quoteAmount = poolSettings.initialLiquiditySOL;
    return (quoteAmount / baseAmount * formData.supply).toFixed(9);
  };

  const calculateDistribution = () => {
    const { distribution } = poolSettings;
    const supply = formData.supply;
    return {
      liquidity: (supply * distribution.liquidity / 100),
      team: (supply * distribution.team / 100),
      marketing: (supply * distribution.marketing / 100),
      treasury: (supply * distribution.treasury / 100)
    };
  };

  const estimateTotalCost = () => {
    const poolCost = raydiumService.estimatePoolCreationCost(poolSettings.poolType);
    const tokenCreationCost = 0.01; // Approximate
    const liquidityAmount = poolSettings.initialLiquiditySOL;
    return (poolCost + tokenCreationCost + liquidityAmount).toFixed(3);
  };

  const handleLaunch = async () => {
    if (!validateForm() || !publicKey) return;

    setLoading(true);
    setStep('creating');

    try {
      const provider = (window as any).solana;
      if (!provider?.isPhantom) {
        throw new Error('Phantom wallet not found');
      }

      const launchConfig = {
        name: formData.name,
        symbol: formData.symbol,
        decimals: formData.decimals,
        supply: formData.supply,
        description: formData.description,
        imageUrl: formData.imageUrl,
        website: formData.website,
        twitter: formData.twitter,
        telegram: formData.telegram,
        poolConfig: {
          poolType: poolSettings.poolType as 'CPMM' | 'AMM_V4' | 'CLMM',
          baseToken: new PublicKey(publicKey), // Convert string to PublicKey
          quoteToken: new PublicKey('So11111111111111111111111111111111111111112'), // SOL
          baseAmount: poolSettings.baseTokenAmount,
          quoteAmount: poolSettings.initialLiquiditySOL,
          feeTier: poolSettings.feeTier,
          startTime: poolSettings.startTime === 'custom' 
            ? new Date(poolSettings.customStartTime!).getTime() / 1000 
            : undefined,
          burnLpTokens: poolSettings.burnAndEarn
        },
        initialLiquiditySOL: poolSettings.initialLiquiditySOL,
        lockLiquidity: poolSettings.lockLiquidity,
        lockDuration: poolSettings.lockDuration
      };

      const result = await raydiumService.launchTokenOnRaydium(
        launchConfig,
        new PublicKey(publicKey), // Convert string to PublicKey
        async (tx) => provider.signTransaction(tx)
      );

      if (result.success) {
        setCreatedTokenAddress(result.tokenAddress || '');
        setPoolAddress(result.poolAddress || '');
        setTxSignature(result.txSignature || '');
        setStep('success');
      } else {
        throw new Error(result.error || 'Failed to launch token');
      }

    } catch (error: any) {
      console.error('Launch failed:', error);
      alert(`Failed to launch token: ${error.message}`);
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      symbol: '',
      description: '',
      supply: 1000000000,
      decimals: 9,
      imageUrl: '',
      website: '',
      twitter: '',
      telegram: '',
      discord: ''
    });
    setPoolSettings({
      poolType: 'CPMM',
      initialLiquiditySOL: 10,
      baseTokenAmount: 800000000,
      feeTier: 25,
      startTime: 'now',
      lockLiquidity: true,
      lockDuration: 30,
      burnAndEarn: true,
      distribution: {
        liquidity: 80,
        team: 10,
        marketing: 5,
        treasury: 5
      }
    });
    setStep('form');
    setCreatedTokenAddress('');
    setPoolAddress('');
    setTxSignature('');
  };

  // Success Screen
  if (step === 'success') {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
        <div className="text-center">
          <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Droplet className="w-10 h-10 text-blue-400" />
          </div>

          <h2 className="text-3xl font-bold mb-3 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Token Launched on Raydium!
          </h2>

          <p className="text-gray-400 mb-8">
            Your token and liquidity pool are now live
          </p>

          {createdTokenAddress && (
            <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
              <div className="text-sm text-gray-400 mb-2">Token Address</div>
              <div className="font-mono text-sm break-all text-blue-400">
                {createdTokenAddress}
              </div>
            </div>
          )}

          {poolAddress && (
            <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
              <div className="text-sm text-gray-400 mb-2">Pool Address</div>
              <div className="font-mono text-sm break-all text-cyan-400">
                {poolAddress}
              </div>
            </div>
          )}

          {txSignature && (
            <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-400 mb-2">Transaction</div>
              <div className="font-mono text-xs break-all">{txSignature}</div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`https://raydium.io/liquidity/increase/?pool_id=${poolAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View on Raydium</span>
            </a>

            <a
              href={`https://solscan.io/tx/${txSignature}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View Transaction</span>
            </a>

            <button
              onClick={resetForm}
              className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
            >
              Launch Another Token
            </button>
          </div>

          {poolSettings.burnAndEarn && (
            <div className="mt-8 p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Flame className="w-5 h-5 text-green-400" />
                <span className="font-medium text-green-400">Burn & Earn Active</span>
              </div>
              <p className="text-sm text-gray-400">
                LP tokens have been burned. You can still collect trading fees.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Creating Screen
  if (step === 'creating') {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin mx-auto mb-6 text-blue-400" />
          <h2 className="text-2xl font-bold mb-3">Creating Your Raydium Pool...</h2>
          <p className="text-gray-400 mb-2">Deploying token and initializing liquidity</p>

          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-center space-x-2 text-sm">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              <span>Creating {formData.symbol} token</span>
            </div>
            <div className="flex items-center justify-center space-x-2 text-sm">
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
              <span>Setting up {poolSettings.poolType} pool</span>
            </div>
            <div className="flex items-center justify-center space-x-2 text-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span>Adding {poolSettings.initialLiquiditySOL} SOL liquidity</span>
            </div>
          </div>

          {poolSettings.lockLiquidity && (
            <div className="mt-4 inline-flex items-center space-x-2 bg-blue-900/20 border border-blue-700/50 rounded-lg px-4 py-2">
              <Lock className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-400">
                Liquidity will be locked for {poolSettings.lockDuration} days
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main Form
  return (
    <>
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-600/20 rounded-lg">
            <Droplet className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Launch on Raydium</h2>
            <p className="text-sm text-gray-400">Professional DEX with deep liquidity</p>
          </div>
        </div>
      </div>

      {/* Pool Type Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-3">Pool Type</label>
        <div className="grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setPoolSettings({ ...poolSettings, poolType: 'CPMM' })}
            className={`p-4 rounded-xl border-2 transition-all ${
              poolSettings.poolType === 'CPMM'
                ? 'bg-blue-600/20 border-blue-500 shadow-lg shadow-blue-500/20'
                : 'bg-gray-900/50 border-gray-700 hover:border-blue-600/50'
            }`}
          >
            <div className="font-semibold mb-1">CPMM</div>
            <div className="text-xs text-gray-400">Latest & Cheapest</div>
            <div className="text-xs text-blue-400 mt-1">~0.15 SOL</div>
          </button>

          <button
            type="button"
            onClick={() => setPoolSettings({ ...poolSettings, poolType: 'AMM_V4' })}
            className={`p-4 rounded-xl border-2 transition-all ${
              poolSettings.poolType === 'AMM_V4'
                ? 'bg-blue-600/20 border-blue-500 shadow-lg shadow-blue-500/20'
                : 'bg-gray-900/50 border-gray-700 hover:border-blue-600/50'
            }`}
          >
            <div className="font-semibold mb-1">AMM V4</div>
            <div className="text-xs text-gray-400">Battle-tested</div>
            <div className="text-xs text-purple-400 mt-1">~0.7 SOL</div>
          </button>

          <button
            type="button"
            onClick={() => setPoolSettings({ ...poolSettings, poolType: 'CLMM' })}
            className={`p-4 rounded-xl border-2 transition-all ${
              poolSettings.poolType === 'CLMM'
                ? 'bg-blue-600/20 border-blue-500 shadow-lg shadow-blue-500/20'
                : 'bg-gray-900/50 border-gray-700 hover:border-blue-600/50'
            }`}
          >
            <div className="font-semibold mb-1">CLMM</div>
            <div className="text-xs text-gray-400">Concentrated</div>
            <div className="text-xs text-cyan-400 mt-1">~0.1 SOL</div>
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
        <div className="flex items-start space-x-2">
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-blue-400 mb-1">
              {poolSettings.poolType === 'CPMM' && 'CPMM Pool Features'}
              {poolSettings.poolType === 'AMM_V4' && 'AMM V4 Pool Features'}
              {poolSettings.poolType === 'CLMM' && 'CLMM Pool Features'}
            </div>
            <ul className="text-xs text-gray-400 space-y-1">
              {poolSettings.poolType === 'CPMM' && (
                <>
                  <li>✓ Lowest creation cost (0.15 SOL)</li>
                  <li>✓ Supports Token-2022 standard</li>
                  <li>✓ Multiple fee tier options</li>
                  <li>✓ Burn & Earn capability</li>
                </>
              )}
              {poolSettings.poolType === 'AMM_V4' && (
                <>
                  <li>✓ Most battle-tested on Solana</li>
                  <li>✓ Includes OpenBook market</li>
                  <li>✓ Maximum compatibility</li>
                  <li>✓ Proven liquidity model</li>
                </>
              )}
              {poolSettings.poolType === 'CLMM' && (
                <>
                  <li>✓ Concentrated liquidity ranges</li>
                  <li>✓ Higher capital efficiency</li>
                  <li>✓ Custom price ranges</li>
                  <li>✓ Best for stable pairs</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Token Name */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Token Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value.slice(0, 50) })}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
            placeholder="My Awesome Token"
            maxLength={50}
          />
          <div className="text-xs text-gray-500 mt-1">
            {formData.name.length}/50 characters
          </div>
        </div>

        {/* Token Symbol */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Token Symbol <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.symbol}
            onChange={(e) => setFormData({ ...formData, symbol: e.target.value.slice(0, 10).toUpperCase() })}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
            placeholder="TOKEN"
            maxLength={10}
          />
          <div className="text-xs text-gray-500 mt-1">
            {formData.symbol.length}/10 characters
          </div>
        </div>

        {/* Supply and Decimals */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Total Supply <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.supply}
              onChange={(e) => setFormData({ ...formData, supply: Number(e.target.value) })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
              placeholder="1000000000"
              min="1"
              max="1000000000000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Decimals
            </label>
            <select
              value={formData.decimals}
              onChange={(e) => setFormData({ ...formData, decimals: Number(e.target.value) })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
            >
              <option value={6}>6</option>
              <option value={9}>9 (Recommended)</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value.slice(0, 500) })}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
            placeholder="Describe your token and its purpose..."
            rows={3}
            maxLength={500}
          />
          <div className="text-xs text-gray-500 mt-1">
            {formData.description.length}/500 characters
          </div>
        </div>

        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Token Logo <span className="text-red-500">*</span>
          </label>
          <div
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={async (e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) await handleFileUpload(file);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`w-full min-h-[140px] rounded-xl border-2 border-dashed transition-all cursor-pointer ${
              isDragging
                ? 'border-blue-400 bg-blue-500/5'
                : 'border-gray-700 bg-gray-900/30 hover:border-blue-500/50'
            } flex items-center justify-center p-6`}
          >
            {!formData.imageUrl && !uploadingLogo && (
              <div className="text-center">
                <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-medium mb-1">
                  Drop your logo here or click to upload
                </p>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF or WEBP (max 5MB)
                </p>
              </div>
            )}

            {uploadingLogo && (
              <div className="flex items-center space-x-3">
                <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                <span className="text-sm">Uploading...</span>
              </div>
            )}

            {formData.imageUrl && !uploadingLogo && (
              <div className="flex items-center gap-4">
                <img
                  src={formData.imageUrl}
                  alt="Token Logo"
                  className="w-20 h-20 rounded-lg object-cover border-2 border-blue-500/30"
                />
                <div className="text-left">
                  <p className="text-sm text-gray-400 mb-2">Logo uploaded</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, imageUrl: '' }));
                      }}
                      className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Remove
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                    >
                      Replace
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) await handleFileUpload(file);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
        </div>

        {/* Social Links - Moved from Advanced Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Website <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Twitter <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.twitter}
              onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
              placeholder="@username or https://twitter.com/..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Telegram <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.telegram}
              onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
              placeholder="t.me/groupname"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Discord <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.discord}
              onChange={(e) => setFormData({ ...formData, discord: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
              placeholder="discord.gg/invite"
            />
          </div>
        </div>

        {/* Liquidity Settings */}
        <div className="border-t border-gray-700 pt-4">
          <h3 className="font-medium mb-4 flex items-center space-x-2">
            <Droplet className="w-4 h-4 text-blue-400" />
            <span>Liquidity Settings</span>
          </h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Initial Liquidity (SOL) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={poolSettings.initialLiquiditySOL}
                onChange={(e) => setPoolSettings({
                  ...poolSettings,
                  initialLiquiditySOL: Number(e.target.value)
                })}
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="10"
                min="1"
                step="1"
              />
              <div className="text-xs text-gray-500 mt-1">
                Minimum: 1 SOL
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Fee Tier
              </label>
              <select
                value={poolSettings.feeTier}
                onChange={(e) => setPoolSettings({
                  ...poolSettings,
                  feeTier: Number(e.target.value)
                })}
                className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500"
              >
                {FEE_TIERS[poolSettings.poolType === 'CLMM' ? 'CLMM' : 'CPMM'].map(tier => (
                  <option key={tier.value} value={tier.value}>
                    {tier.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Token Distribution */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-3">Token Distribution</label>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-400">Liquidity</span>
                  <input
                    type="number"
                    value={poolSettings.distribution.liquidity}
                    onChange={(e) => setPoolSettings({
                      ...poolSettings,
                      distribution: {
                        ...poolSettings.distribution,
                        liquidity: Number(e.target.value)
                      }
                    })}
                    className="w-16 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-xs text-right"
                    min="0"
                    max="100"
                  />
                  <span className="text-xs text-gray-500 ml-1">%</span>
                </div>
                <div className="text-sm font-mono text-blue-400">
                  {(formData.supply * poolSettings.distribution.liquidity / 100).toLocaleString()}
                </div>
              </div>

              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-400">Team</span>
                  <input
                    type="number"
                    value={poolSettings.distribution.team}
                    onChange={(e) => setPoolSettings({
                      ...poolSettings,
                      distribution: {
                        ...poolSettings.distribution,
                        team: Number(e.target.value)
                      }
                    })}
                    className="w-16 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-xs text-right"
                    min="0"
                    max="100"
                  />
                  <span className="text-xs text-gray-500 ml-1">%</span>
                </div>
                <div className="text-sm font-mono text-gray-400">
                  {(formData.supply * poolSettings.distribution.team / 100).toLocaleString()}
                </div>
              </div>

              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-400">Marketing</span>
                  <input
                    type="number"
                    value={poolSettings.distribution.marketing}
                    onChange={(e) => setPoolSettings({
                      ...poolSettings,
                      distribution: {
                        ...poolSettings.distribution,
                        marketing: Number(e.target.value)
                      }
                    })}
                    className="w-16 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-xs text-right"
                    min="0"
                    max="100"
                  />
                  <span className="text-xs text-gray-500 ml-1">%</span>
                </div>
                <div className="text-sm font-mono text-gray-400">
                  {(formData.supply * poolSettings.distribution.marketing / 100).toLocaleString()}
                </div>
              </div>

              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-400">Treasury</span>
                  <input
                    type="number"
                    value={poolSettings.distribution.treasury}
                    onChange={(e) => setPoolSettings({
                      ...poolSettings,
                      distribution: {
                        ...poolSettings.distribution,
                        treasury: Number(e.target.value)
                      }
                    })}
                    className="w-16 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-xs text-right"
                    min="0"
                    max="100"
                  />
                  <span className="text-xs text-gray-500 ml-1">%</span>
                </div>
                <div className="text-sm font-mono text-gray-400">
                  {(formData.supply * poolSettings.distribution.treasury / 100).toLocaleString()}
                </div>
              </div>
            </div>
            {(poolSettings.distribution.liquidity + 
              poolSettings.distribution.team + 
              poolSettings.distribution.marketing + 
              poolSettings.distribution.treasury) !== 100 && (
              <div className="text-xs text-red-400 mt-2">
                Distribution must total 100% (currently {
                  poolSettings.distribution.liquidity + 
                  poolSettings.distribution.team + 
                  poolSettings.distribution.marketing + 
                  poolSettings.distribution.treasury
                }%)
              </div>
            )}
          </div>

          {/* Security Features */}
          <div className="space-y-3">
            {/* Burn & Earn */}
            <div className="bg-green-900/10 border border-green-700/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="burn-earn"
                  checked={poolSettings.burnAndEarn}
                  onChange={(e) => setPoolSettings({
                    ...poolSettings,
                    burnAndEarn: e.target.checked
                  })}
                  className="mt-1 w-4 h-4 text-green-600 bg-gray-900 border-gray-700 rounded focus:ring-green-500"
                />
                <label htmlFor="burn-earn" className="flex-1 cursor-pointer">
                  <div className="font-medium flex items-center space-x-2">
                    <Flame className="w-4 h-4 text-green-400" />
                    <span>Burn & Earn</span>
                    <span className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded">RECOMMENDED</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Burn LP tokens to lock liquidity forever while still earning trading fees
                  </p>
                </label>
              </div>
            </div>

            {/* Liquidity Lock */}
            <div className="bg-blue-900/10 border border-blue-700/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="lock-liquidity"
                  checked={poolSettings.lockLiquidity}
                  onChange={(e) => setPoolSettings({
                    ...poolSettings,
                    lockLiquidity: e.target.checked
                  })}
                  className="mt-1 w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                />
                <label htmlFor="lock-liquidity" className="flex-1 cursor-pointer">
                  <div className="font-medium flex items-center space-x-2">
                    <Lock className="w-4 h-4 text-blue-400" />
                    <span>Lock Liquidity</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Time-lock LP tokens to prevent rug pulls
                  </p>
                </label>
              </div>

              {poolSettings.lockLiquidity && (
                <div className="mt-3 ml-7">
                  <label className="block text-sm font-medium mb-2">
                    Lock Duration (days)
                  </label>
                  <input
                    type="number"
                    value={poolSettings.lockDuration}
                    onChange={(e) => setPoolSettings({
                      ...poolSettings,
                      lockDuration: Number(e.target.value)
                    })}
                    className="w-full px-3 py-1.5 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500"
                    min="7"
                    max="365"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Launch Time */}
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">Launch Time</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setPoolSettings({ ...poolSettings, startTime: 'now' })}
                className={`py-2 px-4 rounded-lg border transition-all ${
                  poolSettings.startTime === 'now'
                    ? 'bg-blue-600/20 border-blue-500'
                    : 'bg-gray-900/50 border-gray-700 hover:border-blue-600/50'
                }`}
              >
                Launch Now
              </button>
              <button
                type="button"
                onClick={() => setPoolSettings({ ...poolSettings, startTime: 'custom' })}
                className={`py-2 px-4 rounded-lg border transition-all ${
                  poolSettings.startTime === 'custom'
                    ? 'bg-blue-600/20 border-blue-500'
                    : 'bg-gray-900/50 border-gray-700 hover:border-blue-600/50'
                }`}
              >
                Schedule Launch
              </button>
            </div>

            {poolSettings.startTime === 'custom' && (
              <input
                type="datetime-local"
                value={poolSettings.customStartTime}
                onChange={(e) => setPoolSettings({
                  ...poolSettings,
                  customStartTime: e.target.value
                })}
                className="w-full mt-3 px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500"
              />
            )}
          </div>
        </div>

        {/* Advanced Settings */}
        <div className="border-t border-gray-700 pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-between w-full py-2 text-left"
          >
            <span className="font-medium flex items-center space-x-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span>Advanced Launch Settings</span>
            </span>
            <span className="text-gray-400">
              {showAdvanced ? '−' : '+'}
            </span>
          </button>

          {showAdvanced && (
            <div className="mt-4 space-y-4">
              {/* Bundle Selection */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium flex items-center space-x-2">
                    <Package className="w-4 h-4 text-purple-400" />
                    <span>Bundle Auto-Buy (Optional)</span>
                  </h4>
                  {selectedBundle && (
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      isUnlocked 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {isUnlocked ? '🔓 Unlocked' : '🔒 Locked'}
                    </span>
                  )}
                </div>
                
                {bundles.length > 0 ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {bundles.map((bundle) => {
                        const eligibleWallets = bundle.wallets.filter(
                          (w: any) => w.balance >= (poolSettings.initialLiquiditySOL * 0.001 + 0.002)
                        );
                        
                        return (
                          <button
                            key={bundle.id}
                            type="button"
                            onClick={() => {
                              setSelectedBundle(bundle);
                              setIsUnlocked(false);
                            }}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              selectedBundle?.id === bundle.id
                                ? 'border-purple-500 bg-purple-500/10'
                                : 'border-gray-700 bg-gray-900/30 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-medium">{bundle.name}</div>
                              {selectedBundle?.id === bundle.id && (
                                <Check className="w-4 h-4 text-purple-400" />
                              )}
                            </div>
                            <div className="text-xs text-gray-400 space-y-1">
                              <div>{bundle.wallets.length} wallets • {bundle.totalBalance.toFixed(3)} SOL</div>
                              <div className={eligibleWallets.length > 0 ? 'text-green-400' : 'text-red-400'}>
                                {eligibleWallets.length} wallets funded for auto-buy
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    
                    {selectedBundle && !isUnlocked && (
                      <button
                        type="button"
                        onClick={() => setShowBundlePasswordModal(true)}
                        className="w-full px-4 py-2 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30 transition-all"
                      >
                        Unlock Bundle for Auto-Buy
                      </button>
                    )}
                    
                    {selectedBundle && isUnlocked && (
                      <div className="p-3 bg-green-900/20 border border-green-700/30 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium text-green-400">Bundle Ready</div>
                            <div className="text-xs text-gray-400 mt-1">
                              {selectedBundle.wallets.filter((w: any) => 
                                w.balance >= (poolSettings.initialLiquiditySOL * 0.001 + 0.002)
                              ).length} wallets will buy after pool creation
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-gray-400">
                    No bundles available. Create a bundle in Advanced Tools to enable auto-buying.
                  </div>
                )}
              </div>

              {/* MEV Protection */}
              <div className="bg-purple-900/10 border border-purple-700/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="mev-protection"
                    className="mt-1 w-4 h-4 text-purple-600 bg-gray-900 border-gray-700 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="mev-protection" className="flex-1 cursor-pointer">
                    <div className="font-medium flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-purple-400" />
                      <span>MEV Protection (Jito Bundle)</span>
                      <span className="text-xs bg-purple-600/20 text-purple-400 px-2 py-0.5 rounded">COMING SOON</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Protect against sandwich attacks during pool creation
                    </p>
                  </label>
                </div>
              </div>

              {/* Initial Price Preview */}
              <div className="bg-gray-900/50 rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-2">Initial Price Calculation</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Tokens in Pool:</span>
                    <span className="font-mono">{poolSettings.baseTokenAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>SOL in Pool:</span>
                    <span className="font-mono">{poolSettings.initialLiquiditySOL}</span>
                  </div>
                  <div className="border-t border-gray-700 pt-2">
                    <div className="flex justify-between font-medium">
                      <span>Initial Price:</span>
                      <span className="text-blue-400 font-mono">
                        {calculateInitialPrice()} SOL per {formData.symbol || 'TOKEN'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Cost Breakdown */}
        <div className="bg-gray-900/50 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <DollarSign className="w-4 h-4 text-blue-400 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-blue-400 mb-3">Total Cost Breakdown</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Token Creation:</span>
                  <span className="font-mono">~0.01 SOL</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{poolSettings.poolType} Pool:</span>
                  <span className="font-mono">{raydiumService.estimatePoolCreationCost(poolSettings.poolType)} SOL</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Initial Liquidity:</span>
                  <span className="font-mono">{poolSettings.initialLiquiditySOL} SOL</span>
                </div>
                {poolSettings.lockLiquidity && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Lock Service:</span>
                    <span className="font-mono">~0.01 SOL</span>
                  </div>
                )}
                <div className="border-t border-gray-700 pt-2">
                  <div className="flex justify-between font-medium">
                    <span>Estimated Total:</span>
                    <span className="text-blue-400 font-mono">
                      {estimateTotalCost()} SOL
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-3">
                * Actual costs may vary slightly based on network conditions
              </div>
            </div>
          </div>
        </div>

        {/* Launch Button */}
        <button
          onClick={handleLaunch}
          disabled={loading || !isConnected}
          className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl font-semibold text-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 shadow-lg hover:shadow-blue-500/20"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Launching...</span>
            </>
          ) : (
            <>
              <Rocket className="w-5 h-5" />
              <span>Launch on Raydium</span>
              <Droplet className="w-5 h-5" />
            </>
          )}
        </button>

          {!isConnected && (
            <p className="text-center text-sm text-yellow-400 mt-3 flex items-center justify-center space-x-2">
              <AlertCircle className="w-4 h-4" />
              <span>Please connect your wallet to launch</span>
            </p>
          )}
        </div>
      </div>

      {/* Bundle Password Modal - Rendered outside main container */}
      {showBundlePasswordModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 p-6 rounded-xl max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Unlock Bundle</h3>
            <p className="text-sm text-gray-400 mb-4">
              Enter password to unlock "{selectedBundle?.name}" for auto-buying
            </p>
            
            <input
              type="password"
              value={bundlePassword}
              onChange={(e) => setBundlePassword(e.target.value)}
              className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:border-purple-500"
              placeholder="Bundle password"
              onKeyPress={(e) => e.key === 'Enter' && unlockSelectedBundle()}
            />
            
            <div className="flex gap-3">
              <button
                onClick={unlockSelectedBundle}
                className="flex-1 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90"
              >
                Unlock
              </button>
              <button
                onClick={() => {
                  setShowBundlePasswordModal(false);
                  setBundlePassword('');
                }}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RaydiumCreationForm;